import { AiChatType, LibraryItemType } from "@src/types.generated";
import {
  StartGenAuditFromOutlineChatWorkflowDocument,
  StartGenCheckInFromOutlineChatWorkflowDocument,
  StartGenTaskListFromOutlineChatWorkflowDocument,
} from "@src/features/assistantChat/aiChat.generated";
import { DocumentNode } from "graphql";

export enum OutlineFlowType {
  TaskList = "TaskList",
  CheckIn = "CheckIn",
  Audit = "Audit",
}

export interface OutlineFlowConfig {
  type: OutlineFlowType;
  chatType: AiChatType;
  libraryItemType: LibraryItemType;
  texts: {
    dialogTitle: string;
    contentTitle: string;
    contentDescription: string;
    placeholder: string;
    newFromScratchLabel: string;
    canvasSubtitle: string;
    generateActionLabel: string;
    threadEmptyStateTitle: string;
    threadEmptyStateSubtitle: string;
    composerPlaceholder: string;
  };
  callsites: {
    createDialog: string;
    startGenWorkflow: string;
    genWorkflowFailed: string;
  };
  mutation: {
    document: DocumentNode;
    outlineTextFieldName:
      | "taskListOutlineText"
      | "checkInOutlineText"
      | "auditOutlineText";
  };
}

export const getOutlineFlows = (): Record<
  OutlineFlowType,
  OutlineFlowConfig
> => ({
  [OutlineFlowType.TaskList]: {
    type: OutlineFlowType.TaskList,
    chatType: AiChatType.TaskListOutline,
    libraryItemType: LibraryItemType.Task,
    texts: {
      dialogTitle: `New Checklist`,
      contentTitle: `Create checklist with AI`,
      contentDescription: `Describe your operational tasks, procedures, or checklists.\nOpus AI will turn them into a complete editable checklist form.`,
      placeholder:
        "Example: Daily refrigerator temperature checks - record temps for walk-in fridge, freezer, and prep stations. Safety checklist PDF is attached.",
      newFromScratchLabel: `New checklist from scratch`,
      canvasSubtitle: `Checklist outline`,
      generateActionLabel: `Generate checklist`,
      threadEmptyStateTitle: `Create checklist with AI`,
      threadEmptyStateSubtitle: `Describe your operational tasks, procedures, or checklists.\nOpus AI will turn them into a complete editable checklist form.`,
      composerPlaceholder: `Describe your checklist...`,
    },
    callsites: {
      createDialog: "create_task_list_dialog",
      startGenWorkflow: "start_task_list_generation_workflow_from_ai_chat",
      genWorkflowFailed: "task_list_generation_workflow_failed",
    },
    mutation: {
      document: StartGenTaskListFromOutlineChatWorkflowDocument,
      outlineTextFieldName: "taskListOutlineText",
    },
  },
  [OutlineFlowType.CheckIn]: {
    type: OutlineFlowType.CheckIn,
    chatType: AiChatType.CheckInOutline,
    libraryItemType: LibraryItemType.Skill,
    texts: {
      dialogTitle: "New Check-in",
      contentTitle: "Create check-in with AI",
      contentDescription:
        "Describe your check-in requirements, questions, and evaluation criteria.\nOpus AI will turn them into a complete editable check-in form.",
      placeholder:
        "Example: Barista latte art check-in - evaluate milk steaming technique, pouring consistency, and presentation quality.",
      newFromScratchLabel: "New check-in from scratch",
      canvasSubtitle: "Check-in outline",
      generateActionLabel: "Generate check-in",
      threadEmptyStateTitle: "Create check-in with AI",
      threadEmptyStateSubtitle:
        "Describe your check-in requirements, questions, and evaluation criteria.\nOpus AI will turn them into a complete editable check-in form.",
      composerPlaceholder: "Describe your check-in...",
    },
    callsites: {
      createDialog: "create_skill_dialog",
      startGenWorkflow: "start_skill_generation_workflow_from_ai_chat",
      genWorkflowFailed: "skill_generation_workflow_failed",
    },
    mutation: {
      document: StartGenCheckInFromOutlineChatWorkflowDocument,
      outlineTextFieldName: "checkInOutlineText",
    },
  },
  [OutlineFlowType.Audit]: {
    type: OutlineFlowType.Audit,
    chatType: AiChatType.AuditOutline,
    libraryItemType: LibraryItemType.Audit,
    texts: {
      dialogTitle: "New Audit",
      contentTitle: "Create audit with AI",
      contentDescription:
        "Describe your audit requirements, evaluation criteria, and scoring system.\nOpus AI will turn them into a complete editable audit form.",
      placeholder:
        "Example: Food safety audit - evaluate temperature logs, cleanliness standards, and compliance with health regulations.",
      newFromScratchLabel: "New audit from scratch",
      canvasSubtitle: "Audit outline",
      generateActionLabel: "Generate audit",
      threadEmptyStateTitle: "Create audit with AI",
      threadEmptyStateSubtitle:
        "Describe your audit requirements, evaluation criteria, and scoring system.\nOpus AI will turn them into a complete editable audit form.",
      composerPlaceholder: "Describe your audit...",
    },
    callsites: {
      createDialog: "create_audit_dialog",
      startGenWorkflow: "start_audit_generation_workflow_from_ai_chat",
      genWorkflowFailed: "audit_generation_workflow_failed",
    },
    mutation: {
      document: StartGenAuditFromOutlineChatWorkflowDocument,
      outlineTextFieldName: "auditOutlineText",
    },
  },
});

export const useOutlineFlow = (flowType: OutlineFlowType): OutlineFlowConfig =>
  getOutlineFlows()[flowType];
